import type { FacilityEntity, FacilityEntityType } from '@/types/facility';
import { ENTITY_RENDER_CONFIG } from '@/types/facility';

/**
 * Helpers for type-scoped auto-numbering of facility entities.
 *
 * Why this exists
 * ---------------
 * Before this module, the Facility Editor numbered new parking rows from 1
 * each time:
 *
 *   handleAddEntity:        label = `${displayLabel} ${entities.length + 1}` // total count, not type-scoped
 *   generateParkingRow:     makeBay(..., i)                                  // always 0..count-1
 *
 * For a shop with `Parking 1` through `Parking 4` already, adding a row of
 * 7 produced `Parking 1` through `Parking 7` — four duplicates. The
 * `(layout_id, label)` constraint on `parking_lot_spots` then turned each
 * duplicate into the safety-fallback `<name> #<8hex>` (mirror trigger,
 * migration 20260601140000) and Birdseye rendered the ugly labels.
 *
 * The mirror trigger's suffix is intentionally kept as a last-resort
 * protection. This module makes it irrelevant under normal usage by:
 *   1. `nextLabelNumber(entities, type)` — returns max + 1 of the existing
 *      `<displayLabel> <N>` labels for that type only.
 *   2. `normalizeParkingLabels(entities)` — load-time cleanup that strips
 *      any stale ` #<hex>` suffix and renumbers in-memory duplicates to
 *      unique values. Called once per load; flags `changed` so the editor
 *      can mark itself dirty and prompt a save that propagates the clean
 *      names back to `shop_bays.name` (and through the trigger to
 *      `parking_lot_spots.label`).
 */

// Escape user-provided strings for regex composition. `displayLabel`
// shouldn't contain regex metacharacters in practice, but defending
// against typos like "Wash Bay" (no metacharacters) vs a hypothetical
// "Lift+Press" (has +) is essentially free.
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Match `<displayLabel> <N>` with an optional trailing ` #<hex>` suffix.
 * The suffix is what the migration trigger appends on (layout_id, label)
 * collisions; this regex tolerates it so a previously-suffixed label
 * still contributes its number to the max + 1 calculation.
 */
function labelPattern(displayLabel: string): RegExp {
  return new RegExp(
    `^${escapeRegex(displayLabel)}\\s+(\\d+)(?:\\s+#[0-9a-f]{1,32})?\\s*$`,
    'i',
  );
}

/**
 * Returns the next available `<displayLabel> <N>` number for the given
 * entity type, computed as `max(N over entities of that type) + 1`.
 * Returns 1 when no existing entity matches the pattern.
 *
 * Max + 1 (rather than first-gap) matches what the user expects from
 * predictable shop-floor numbering: deleting "Parking 7" and then adding
 * a new spot should produce "Parking 12" (next number after "Parking 11"),
 * not reuse the gap.
 */
export function nextLabelNumber(
  entities: FacilityEntity[],
  type: FacilityEntityType,
): number {
  const cfg = ENTITY_RENDER_CONFIG[type];
  if (!cfg) return 1;
  const re = labelPattern(cfg.displayLabel);

  let max = 0;
  for (const e of entities) {
    if (e.entityType !== type) continue;
    const m = re.exec(e.label);
    if (!m) continue;
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

/**
 * Load-time cleanup pass for parking labels.
 *
 * Two cleanup behaviors layered together:
 *   1. Strip the deterministic ` #<hex>` suffix where it appears in
 *      shop_bays.name (rare — the trigger only stamps it on
 *      parking_lot_spots.label, but if a prior code path mirrored the
 *      suffix back into shop_bays we want to undo it).
 *   2. Renumber in-memory duplicate names so each entity ends up with a
 *      unique `Parking <N>` label. The first occurrence keeps its number;
 *      every subsequent collision gets `max + 1` (and so on).
 *
 * Non-parking entities pass through untouched. The returned `changed`
 * count tells the caller how many entities were rewritten so it can
 * surface a "needs save" toast — saving propagates the corrected
 * shop_bays.name through the mirror trigger, which then updates
 * parking_lot_spots.label to the clean name.
 *
 * The function does NOT touch parking_lot_spots directly. Doing so would
 * risk breaking `tickets.parking_spot_id` references — the trigger is
 * the only path that updates the mirror and it preserves ids.
 */
export function normalizeParkingLabels(
  entities: FacilityEntity[],
): { entities: FacilityEntity[]; changed: number } {
  const cfg = ENTITY_RENDER_CONFIG['parking_space'];
  if (!cfg) return { entities, changed: 0 };
  const re = labelPattern(cfg.displayLabel);

  // First pass: find the highest number already claimed by either a
  // clean OR a suffixed parking label. We use this as the starting
  // point for fresh numbers handed to duplicates.
  let max = 0;
  for (const e of entities) {
    if (e.entityType !== 'parking_space') continue;
    const m = re.exec(e.label);
    if (!m) continue;
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  }

  // Second pass: walk entities in their existing order; the FIRST entity
  // claiming each clean number keeps it, subsequent duplicates get fresh
  // numbers from max + 1 onward. Stable order keeps the visually "first"
  // spot in a row as the one that retains the natural label.
  const used = new Set<string>();
  let changed = 0;
  const result = entities.map(e => {
    if (e.entityType !== 'parking_space') return e;
    const m = re.exec(e.label);
    if (!m) return e; // unparseable parking label — leave alone

    const cleanLabel = `${cfg.displayLabel} ${m[1]}`;

    if (!used.has(cleanLabel)) {
      used.add(cleanLabel);
      if (e.label !== cleanLabel) {
        // Was suffixed; strip back to the clean form.
        changed += 1;
        return { ...e, label: cleanLabel };
      }
      return e;
    }

    // Duplicate or label-collision — assign the next free number.
    max += 1;
    const newLabel = `${cfg.displayLabel} ${max}`;
    used.add(newLabel);
    changed += 1;
    return { ...e, label: newLabel, bayNumber: max };
  });

  return { entities: result, changed };
}
